import { useCallback, useMemo } from "react";
import {
  EntitySelector,
  EntitySelectorContent,
  EntitySelectorEmpty,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorTrigger,
  Icon,
  type EntityItemConfig,
  type EntityPoolConfig,
  type EntitySelection,
  type EntitySelectorConfig,
  type Omittable,
} from "@salestrak/ui";

import { IssueTypePool } from "#app/components/entity-selectors/issue-type-pool";
import { poolItemVisual } from "#app/components/entity-selectors/utils";
import { useWorkspaceData } from "#app/lib/data/workspace-data-provider";
import { scopeWorkflowIds, type IssueScope } from "#app/lib/filters/issue-scope";
import type { FilterFieldType } from "#app/lib/filters/types";
import {
  buildQuickFilterOptions,
  type QuickFilterOption,
} from "#app/components/filters/quick-inputs/quick-filter-options";

type FilterEntitySelectInputProps = Omittable<{
  /** The filter type; its values come from `buildQuickFilterOptions`, the
   *  same set the Simple panel's search, chips and rows read. */
  type: FilterFieldType;
  value: unknown;
  onChange: (value: unknown) => void;
  /** The screen's scope. A stage of a workflow the scope excludes can match
   *  nothing the screen shows, so the list holds only the scoped workflows'
   *  stages — and under a single workflow drops the workflow name each row
   *  would otherwise repeat. */
  scope?: IssueScope;
  poolLabel: string;
  placeholder: string;
  searchPlaceholder: string;
  emptyMessage: string;
}>;

/** One pool id per input, so a selection can name the pool it came from. */
function poolIdFor(type: FilterFieldType): string {
  return `filter-${type}`;
}

/**
 * The advanced panel's picker for any filter whose values are an enumerable
 * set — stages, Project types, follow-up statuses. The options carry their
 * own read (`selected`) and write (`toggle`) of the filter value, so this
 * component never restates how a value is encoded: the follow-up filter's
 * updates-off `null` member rides the same path as a stage id.
 */
export function FilterEntitySelectInput({
  type,
  value,
  onChange,
  scope,
  poolLabel,
  placeholder,
  searchPlaceholder,
  emptyMessage,
}: FilterEntitySelectInputProps) {
  const pools = useWorkspaceData();
  const poolId = poolIdFor(type);

  const scopedKey = scope ? scopeWorkflowIds(scope)?.join(",") : undefined;

  const options = useMemo<QuickFilterOption[]>(() => {
    const all = buildQuickFilterOptions(type, pools);
    if (scopedKey === undefined) return all;
    const allowed = new Set(scopedKey.split(","));
    return all.filter((option) => {
      const entity = option.entity;
      if (!entity || entity.kind !== "stage") return true;
      return entity.workflowId !== undefined && allowed.has(entity.workflowId);
    });
  }, [type, pools, scopedKey]);

  // One workflow in scope: every stage row would repeat its name, so none
  // carries it.
  const describe = scopedKey === undefined || scopedKey.split(",").length > 1;

  const items = useMemo<EntityItemConfig[]>(
    () =>
      options.map((option) => {
        const { entity, icon, iconColor } = option;
        // An entity paints its own visual; a plain value (a follow-up
        // status) wears the glyph and hue its chips and clocks paint.
        const avatar: EntityItemConfig["avatar"] = entity
          ? { render: () => poolItemVisual(entity) }
          : icon
            ? { render: () => <Icon name={icon} color={iconColor} size="sm" /> }
            : undefined;
        return {
          id: option.key,
          title: option.label,
          description: describe ? option.group : undefined,
          keywords: option.keywords,
          avatar,
        };
      }),
    [options, describe],
  );

  const pool = useMemo<EntityPoolConfig>(
    () => ({ id: poolId, label: poolLabel, items }),
    [poolId, poolLabel, items],
  );

  const config = useMemo<EntitySelectorConfig>(
    () => ({
      pools: [pool],
      maxSelections: 0,
      searchPlaceholder,
      emptyMessage,
    }),
    [pool, searchPlaceholder, emptyMessage],
  );

  const selections = useMemo<EntitySelection[]>(() => {
    const byKey = new Map(items.map((item) => [item.id, item]));
    return options
      .filter((option) => option.selected(value))
      .flatMap((option) => {
        const entity = byKey.get(option.key);
        return entity ? [{ id: option.key, poolId, entity }] : [];
      });
  }, [options, items, value, poolId]);

  const handleChange = useCallback(
    (next: EntitySelection[]) => {
      const wanted = new Set(next.map((s) => s.id));
      let draft = value;
      for (const option of options) {
        if (option.selected(draft) !== wanted.has(option.key)) {
          draft = option.toggle(draft);
        }
      }
      onChange(draft ?? null);
    },
    [options, value, onChange],
  );

  return (
    <EntitySelector config={config} value={selections} onValueChange={handleChange} title={poolLabel}>
      {/* The trigger's size scale bottoms out at "sm". */}
      <EntitySelectorTrigger placeholder={placeholder} size="sm" chips />
      <EntitySelectorContent fit>
        <EntitySelectorInput />
        {/* The Project-types pool carries the door to the Types settings
            page, as every type picker does. */}
        {type === "issue-type" ? (
          <IssueTypePool value={poolId} />
        ) : (
          <EntitySelectorPool value={poolId} />
        )}
        <EntitySelectorEmpty>{emptyMessage}</EntitySelectorEmpty>
      </EntitySelectorContent>
    </EntitySelector>
  );
}
